import { useState, useCallback, useRef, useMemo } from "react";

// Zoom constants
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4.0;
export const ZOOM_DEFAULT = 1.0;
export const ZOOM_STEP = 0.25;

// Storage key for localStorage persistence
const STORAGE_KEY = "report-builder-zoom";

/**
 * Zoom state interface
 */
export interface ZoomState {
  /** Zoom level from 0.25 to 4.0 (25% to 400%) */
  level: number;
  /** Horizontal pan offset in pixels */
  panX: number;
  /** Vertical pan offset in pixels */
  panY: number;
}

/**
 * Zoom actions interface
 */
export interface ZoomActions {
  /** Set zoom to a specific level (will be rounded to nearest 25%) */
  setZoom: (level: number) => void;
  /** Zoom in by one step (25%) */
  zoomIn: () => void;
  /** Zoom out by one step (25%) */
  zoomOut: () => void;
  /** Reset zoom to 100% and clear pan */
  resetZoom: () => void;
  /**
   * Fit zoom to a specific page size within the wrapper container.
   * @param pageWidth - The page width in pixels
   * @param pageHeight - The page height in pixels
   * @param wrapperSelector - CSS selector for the wrapper element (default: ".selecto-wrapper")
   */
  fitToSize: (
    pageWidth: number,
    pageHeight: number,
    wrapperSelector?: string
  ) => void;
  /** Set pan offset */
  setPan: (x: number, y: number) => void;
  /** Reset pan to (0, 0) */
  resetPan: () => void;
}

/**
 * Return type for useZoom hook
 */
export interface UseZoomReturn {
  /** Current zoom state */
  zoom: ZoomState;
  /** Zoom action methods */
  zoomActions: ZoomActions;
  /** Round a zoom level to the nearest 25% increment */
  roundToZoomStep: (level: number) => number;
  /** Minimum zoom level (0.25 = 25%) */
  ZOOM_MIN: number;
  /** Maximum zoom level (4.0 = 400%) */
  ZOOM_MAX: number;
  /** Zoom step increment (0.25 = 25%) */
  ZOOM_STEP: number;
}

/**
 * Round zoom level to nearest 25% increment
 */
export const roundToZoomStep = (level: number): number => {
  return Math.round(level / ZOOM_STEP) * ZOOM_STEP;
};

/**
 * Initialize zoom from localStorage or default
 */
const initializeZoom = (): ZoomState => {
  // Check if we're in a browser environment (not SSR)
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return { level: ZOOM_DEFAULT, panX: 0, panY: 0 };
  }

  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        level: Math.max(
          ZOOM_MIN,
          Math.min(ZOOM_MAX, parsed.level || ZOOM_DEFAULT)
        ),
        panX: parsed.panX || 0,
        panY: parsed.panY || 0,
      };
    }
  } catch (error) {
    console.warn("Failed to load zoom state from localStorage:", error);
  }
  return { level: ZOOM_DEFAULT, panX: 0, panY: 0 };
};

/**
 * Custom hook for managing zoom state with localStorage persistence.
 *
 * Features:
 * - Zoom levels from 25% to 400%
 * - All zoom values rounded to 25% increments
 * - Debounced localStorage persistence
 * - Pan offset support
 * - Fit-to-size calculation for responsive canvas
 *
 * @example
 * ```tsx
 * const { zoom, zoomActions } = useZoom();
 *
 * // Zoom controls
 * <button onClick={zoomActions.zoomIn}>+</button>
 * <span>{Math.round(zoom.level * 100)}%</span>
 * <button onClick={zoomActions.zoomOut}>-</button>
 *
 * // Fit to page
 * zoomActions.fitToSize(pageWidth, pageHeight);
 * ```
 */
export function useZoom(): UseZoomReturn {
  const [zoom, setZoomState] = useState<ZoomState>(initializeZoom);

  // Debounced persist zoom state to localStorage
  const persistZoomTimeoutRef = useRef<number | null>(null);

  const persistZoom = useCallback((newZoom: ZoomState) => {
    // Only persist in browser environment
    if (typeof window === "undefined" || typeof localStorage === "undefined") {
      return;
    }

    // Clear existing timeout
    if (persistZoomTimeoutRef.current) {
      clearTimeout(persistZoomTimeoutRef.current);
    }

    // Debounce localStorage writes to avoid excessive I/O
    persistZoomTimeoutRef.current = window.setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newZoom));
      } catch (error) {
        console.warn("Failed to save zoom state to localStorage:", error);
      }
    }, 300); // 300ms debounce
  }, []);

  const zoomActions = useMemo<ZoomActions>(
    () => ({
      setZoom: (level: number) => {
        const roundedLevel = roundToZoomStep(level);
        const clampedLevel = Math.max(
          ZOOM_MIN,
          Math.min(ZOOM_MAX, roundedLevel)
        );
        const newZoom = { ...zoom, level: clampedLevel };
        setZoomState(newZoom);
        persistZoom(newZoom);
      },

      zoomIn: () => {
        const newLevel = Math.min(ZOOM_MAX, zoom.level + ZOOM_STEP);
        const newZoom = { ...zoom, level: newLevel };
        setZoomState(newZoom);
        persistZoom(newZoom);
      },

      zoomOut: () => {
        const newLevel = Math.max(ZOOM_MIN, zoom.level - ZOOM_STEP);
        const newZoom = { ...zoom, level: newLevel };
        setZoomState(newZoom);
        persistZoom(newZoom);
      },

      resetZoom: () => {
        const newZoom = { level: ZOOM_DEFAULT, panX: 0, panY: 0 };
        setZoomState(newZoom);
        persistZoom(newZoom);
      },

      fitToSize: (
        pageWidth: number,
        pageHeight: number,
        wrapperSelector = ".selecto-wrapper"
      ) => {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          const canvasWrapper = document.querySelector(wrapperSelector);
          if (!canvasWrapper) {
            // Retry if wrapper not found
            setTimeout(() => {
              const retryWrapper = document.querySelector(wrapperSelector);
              if (retryWrapper) {
                const wrapperRect = retryWrapper.getBoundingClientRect();
                if (wrapperRect.width > 0 && wrapperRect.height > 0) {
                  const availableWidth = wrapperRect.width - 50; // Minimal padding for edge spacing
                  const availableHeight = wrapperRect.height - 50;
                  const scaleX = availableWidth / pageWidth;
                  const scaleY = availableHeight / pageHeight;
                  const optimalZoom = Math.min(scaleX, scaleY, ZOOM_MAX);
                  const roundedZoom = roundToZoomStep(optimalZoom);
                  const clampedZoom = Math.max(ZOOM_MIN, roundedZoom);
                  const autoFitZoom = { level: clampedZoom, panX: 0, panY: 0 };
                  setZoomState(autoFitZoom);
                  persistZoom(autoFitZoom);
                }
              }
            }, 200);
            return;
          }

          const wrapperRect = canvasWrapper.getBoundingClientRect();

          // Ensure we have valid dimensions
          if (wrapperRect.width <= 0 || wrapperRect.height <= 0) {
            console.warn(
              "Canvas wrapper has invalid dimensions for fit to size"
            );
            return;
          }

          // Use actual canvas wrapper dimensions with padding
          const availableWidth = wrapperRect.width - 50; // Minimal padding for edge spacing
          const availableHeight = wrapperRect.height - 50;

          // Calculate zoom level that fits the canvas in the available space
          const scaleX = availableWidth / pageWidth;
          const scaleY = availableHeight / pageHeight;
          const optimalZoom = Math.min(scaleX, scaleY, ZOOM_MAX);
          const roundedZoom = roundToZoomStep(optimalZoom);
          const clampedZoom = Math.max(ZOOM_MIN, roundedZoom);

          const autoFitZoom = { level: clampedZoom, panX: 0, panY: 0 };
          setZoomState(autoFitZoom);
          persistZoom(autoFitZoom);
        }, 100);
      },

      setPan: (x: number, y: number) => {
        const newZoom = { ...zoom, panX: x, panY: y };
        setZoomState(newZoom);
        persistZoom(newZoom);
      },

      resetPan: () => {
        const newZoom = { ...zoom, panX: 0, panY: 0 };
        setZoomState(newZoom);
        persistZoom(newZoom);
      },
    }),
    [zoom, persistZoom]
  );

  return {
    zoom,
    zoomActions,
    roundToZoomStep,
    ZOOM_MIN,
    ZOOM_MAX,
    ZOOM_STEP,
  };
}

export default useZoom;
